import { Button } from "@loot-os/ui/components/button";
import { Card, CardContent, CardHeader, CardTitle } from "@loot-os/ui/components/card";
import { Input } from "@loot-os/ui/components/input";
import { Label } from "@loot-os/ui/components/label";
import { Navigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";

import loginArt from "../assets/loginArt.webp";
import { useSessionQuery, useSignInMutation } from "../lib/authQueries";

export function LoginPage() {
  const { data: session } = useSessionQuery();
  const signIn = useSignInMutation();
  const [signedIn, setSignedIn] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  // globals.css reserves the scrollbar's track on every page so a growing page
  // doesn't jog sideways. This page never grows, and with classic scrollbars the
  // empty track shows as a strip of background beside the art, so release it here.
  useEffect(() => {
    const rootStyle = document.documentElement.style;
    rootStyle.scrollbarGutter = "auto";
    return () => {
      rootStyle.scrollbarGutter = "";
    };
  }, []);

  // Only after our own sign-in — an already-signed-in visitor keeps the form, and
  // sign-out (which navigates here before its own refresh lands) doesn't bounce back.
  if (signedIn && session) return <Navigate to="/" replace />;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      // Resolves only after the session is in the query cache, so the
      // redirect above can't race the AppLayout guard.
      await signIn.mutateAsync({ email, password });
      setSignedIn(true);
    } catch {
      // The query client has already toasted the failure.
      setBusy(false);
    }
  };

  // Two layouts over one image. Below `lg` the art fills the page behind a
  // translucent card; from `lg` up the form takes a fixed column and the art
  // the rest. The art's subject (the gate) sits right of center, so the crop
  // is anchored there rather than at 50%: a narrow column keeps the gate whole.
  // A phone shows a thinner slice than a tablet, so it needs its own anchor to
  // land the gate behind the card.
  return (
    <div className="relative isolate flex min-h-svh bg-background">
      <div className="flex w-full items-center justify-center p-4 lg:w-[30rem] lg:shrink-0 lg:p-10">
        <Card className="w-full max-w-sm bg-card/80 supports-backdrop-filter:backdrop-blur-md lg:bg-transparent lg:shadow-none lg:ring-0 lg:backdrop-blur-none">
          <CardHeader>
            <CardTitle className="text-lg font-bold tracking-tight lg:text-2xl">
              Loot <span className="text-primary">OS</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="grid gap-4">
            <form onSubmit={submit} className="grid gap-4">
              <div className="grid gap-2">
                <Label htmlFor="login-email">Email</Label>
                <Input
                  id="login-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="login-password">Password</Label>
                <Input
                  id="login-password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  minLength={8}
                  required
                />
              </div>
              <Button type="submit" className="w-full" disabled={busy}>
                {busy ? "Signing in…" : "Sign in"}
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
      <div className="absolute inset-0 -z-10 overflow-hidden lg:relative lg:inset-auto lg:z-0 lg:flex-1">
        <img
          src={loginArt}
          alt=""
          aria-hidden
          decoding="async"
          fetchPriority="high"
          draggable={false}
          className="absolute inset-0 size-full select-none object-cover object-[85%_45%] sm:object-[92%_45%]"
        />
        <div className="absolute inset-0 bg-background/50 lg:hidden" />
        <div className="absolute inset-y-0 left-0 hidden w-48 bg-linear-to-r from-background to-transparent lg:block" />
      </div>
    </div>
  );
}
